import React from "react";
import { Track, Room, LocalAudioTrack } from "livekit-client";
import { useRoomContext, useTracks } from "@livekit/components-react";
import type { LocalUserChoices } from "@livekit/components-react";
import { VoiceControlBar } from "./VoiceControlBar";

interface UnifiedControlBarProps {
    micEnabled: boolean;
    setMicEnabled: (enabled: boolean) => void;
    preJoinAudioTrack: LocalAudioTrack | null;
    shouldConnect: boolean;
    transcriptLength: number;
    displayName: string;
    setUserChoices: (choices: LocalUserChoices) => void;
    setConnectionAttempt: React.Dispatch<React.SetStateAction<number>>;
    agentRequestedRef: React.MutableRefObject<boolean>;
    setRefreshToken: React.Dispatch<React.SetStateAction<number>>;
    roomName: string;
    permissionError: boolean;
    onCheckPermissions: () => void;
    permissionStatus: string;
}

/**
 * Unified control bar that works in both pre-join and connected states.
 * Handles mic state sync with LiveKit, visualizer track selection,
 * and connect/disconnect buttons.
 */
const UnifiedControlBar: React.FC<UnifiedControlBarProps> = ({
    micEnabled,
    setMicEnabled,
    preJoinAudioTrack,
    shouldConnect,
    transcriptLength,
    displayName,
    setUserChoices,
    setConnectionAttempt,
    agentRequestedRef,
    setRefreshToken,
    roomName,
    permissionError,
    onCheckPermissions,
    permissionStatus,
}) => {
    const room = useRoomContext();

    // Get connected track if available
    const tracks = useTracks([Track.Source.Microphone], { onlySubscribed: false });
    const localMicTrackRef = tracks.find(
        (t) => t.participant.isLocal && t.source === Track.Source.Microphone
    );

    // Simple one-way sync: micEnabled state controls the LiveKit track
    React.useEffect(() => {
        if (shouldConnect && room?.localParticipant) {
            room.localParticipant.setMicrophoneEnabled(micEnabled).catch((err) => {
                console.error("Failed to set microphone state:", err);
            });
        }
    }, [micEnabled, shouldConnect, room]);

    const trackForVisualizer = preJoinAudioTrack;
    const trackRefForVisualizer = undefined;
    const isConnectedState = shouldConnect;

    return (
        <>
            <div className={`control-bar-wrapper ${isConnectedState ? "connected" : "prejoin"}`}>
                <VoiceControlBar
                    micEnabled={micEnabled}
                    onMicToggle={setMicEnabled}
                    track={trackForVisualizer}
                    trackRef={trackRefForVisualizer}
                    permissionStatus={permissionStatus}
                    actionText={isConnectedState ? "Leave" : "Join"}
                    onActionClick={() => {
                        if (isConnectedState) {
                            if (room) room.disconnect();
                        } else {
                            setUserChoices({
                                username: displayName,
                                videoEnabled: false,
                                audioEnabled: micEnabled,
                                videoDeviceId: "",
                                audioDeviceId: "",
                            });
                            setConnectionAttempt((prev) => prev + 1);
                            agentRequestedRef.current = false;
                            setRefreshToken((prev) => prev + 1);
                        }
                    }}
                    actionButtonStyle={{
                        backgroundColor: isConnectedState ? "rgba(220, 38, 38, 0.3)" : "rgba(16, 185, 129, 0.3)",
                        color: "white",
                        borderStyle: "solid",
                        borderWidth: "1px",
                        borderColor: isConnectedState ? "rgba(220, 38, 38, 1)" : "rgba(16, 185, 129, 1)",
                        transition: "all 0.3s ease",
                    }}
                />
            </div>

            {permissionError && (
                <div
                    className="permission-retry-text"
                    style={{
                        position: "absolute",
                        bottom: "20px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        color: "#ef4444",
                        fontSize: "0.9rem",
                        fontWeight: 500,
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        zIndex: 50,
                    }}
                >
                    <span>no mic access</span>
                    <span style={{ opacity: 0.5 }}>|</span>
                    <button
                        onClick={onCheckPermissions}
                        style={{
                            background: "none",
                            border: "none",
                            color: "#ef4444",
                            cursor: "pointer",
                            padding: 0,
                            font: "inherit",
                            fontWeight: 600,
                            textDecoration: "none",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
                        onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
                    >
                        check
                    </button>
                </div>
            )}
        </>
    );
};

export default UnifiedControlBar;
